import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
} from '@mui/material';
import { TemplateActions } from '../../../../Services/_actions/Template/template.actions';
import { CampaignListActions } from '../../../../Services/_actions/index';
import SelectDropdown from '../../../Shared/SelectDropdown';

interface ScheduleTemplateProps {
  show: boolean;
  onHide: () => void;
}

const ScheduleTemplate: React.FC<ScheduleTemplateProps> = ({ show, onHide }) => {
  const [isActive, setIsActive] = useState(false);
  const [triggerName, setTriggerName] = useState('');
  const [triggerFrequency, setTriggerFrequency] = useState('');
  const [triggerMode, setTriggerMode] = useState('');
  const [triggerDate, setTriggerDate] = useState('');
  const [triggerTime, setTriggerTime] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [templateOptions, setTemplateOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectionType, setSelectionType] = useState('');
  const [campaignOptions, setCampaignOptions] = useState<{ value: string; label: string }[]>([]);
  const [databaseOptions, setDatabaseOptions] = useState<{ value: string; label: string }[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [customDateRange, setCustomDateRange] = useState({ fromDate: '', toDate: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isTemplateFetched, setIsTemplateFetched] = useState(false);

  const actions = TemplateActions();
  const campaignActions = CampaignListActions();

  const fetchCampaignList = async () => {
    try {
      setIsLoading(true);
      const filter = {
        search: '',
        next_page: 1,
        organisationId: '', // Replace with actual organization ID
      };
      const response = await campaignActions.getCampaignAssignment(filter);
      const options = response?.list?.map((campaign: any) => ({
        value: campaign.proposal_id,
        label: campaign.name,
      }));
      setCampaignOptions(options || []);
    } catch (error) {
      console.error('Error fetching campaign list:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTemplateList = async () => {
    if (isTemplateFetched) return;

    setIsLoading(true);
    try {
      const allTemplates: any[] = [];
      let page = 0;
      let totalTemplates = 0;

      do {
        const filters = {
          search: '',
          page,
          sortOrder: 'desc',
          sortField: 'created_at',
        };

        const response = await actions.getWhatsappTemplateList(filters);
        allTemplates.push(...(response?.list || []));
        totalTemplates = response?.count || 0;
        page += 1;
      } while (allTemplates.length < totalTemplates);

      const options = allTemplates.map((template) => ({
        value: template.id,
        label: template.name,
      }));

      setTemplateOptions(options);
      setIsTemplateFetched(true);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDatabaseList = () => {
    setDatabaseOptions([
      { value: 'db1', label: 'Database 1' },
      { value: 'db2', label: 'Database 2' },
    ]);
  };

  useEffect(() => {
    if (show) {
      if (!isTemplateFetched) {
        fetchTemplateList();
      }
      fetchCampaignList();
      fetchDatabaseList();
    }
  }, [show]);

  const handleSubmit = () => {
    const data = {
      isActive,
      triggerName,
      triggerFrequency,
      triggerMode,
      triggerDate,
      triggerTime,
      selectedTemplate,
      selectionType,
      uploadedFile,
      customDateRange,
    };
    console.log('Schedule Data:', data);
    onHide();
  };

  return (
    <Dialog open={show} onClose={onHide} maxWidth="md" fullWidth>
      <DialogTitle>
        Schedule Template
        <FormControlLabel
          control={
            <Switch
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              color="primary"
            />
          }
          label={isActive ? 'Active' : 'Inactive'}
          style={{ float: 'right' }}
        />
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Trigger Name"
              value={triggerName}
              onChange={(e) => setTriggerName(e.target.value)}
              placeholder="Enter trigger name"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6}>
            <SelectDropdown
              optionsData={templateOptions}
              selectedValue={selectedTemplate}
              placeholder={isLoading ? 'Loading templates...' : 'Select Template'}
              label="Template"
              id="select-template"
              handleSelect={(value) => setSelectedTemplate(value)}
              isClearable={true}
              isLoading={isLoading}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Trigger Frequency"
              select
              value={triggerFrequency}
              onChange={(e) => setTriggerFrequency(e.target.value)}
            >
              <MenuItem value="">Select Frequency</MenuItem>
              <MenuItem value="hourly">Hourly</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Trigger Mode"
              select
              value={triggerMode}
              onChange={(e) => setTriggerMode(e.target.value)}
            >
              <MenuItem value="">Select Mode</MenuItem>
              <MenuItem value="once">Once</MenuItem>
              <MenuItem value="repeat">Repeat</MenuItem>
            </TextField>
          </Grid>
          {triggerMode === 'repeat' && (
            <>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={customDateRange.fromDate}
                  onChange={(e) =>
                    setCustomDateRange({ ...customDateRange, fromDate: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={customDateRange.toDate}
                  onChange={(e) =>
                    setCustomDateRange({ ...customDateRange, toDate: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Trigger Date"
              type="date"
              value={triggerDate}
              onChange={(e) => setTriggerDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Trigger Time"
              type="time"
              value={triggerTime}
              onChange={(e) => setTriggerTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Selection Type"
              select
              value={selectionType}
              onChange={(e) => setSelectionType(e.target.value)}
            >
              <MenuItem value="">Select Type</MenuItem>
              <MenuItem value="campaign">Campaign</MenuItem>
              <MenuItem value="database">Database</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          </Grid>
          {selectionType === 'campaign' && (
            <Grid item xs={12}>
              <SelectDropdown
                optionsData={campaignOptions}
                selectedValue={selectedTemplate}
                placeholder="Select Campaign"
                label="Campaign"
                id="select-campaign"
                handleSelect={(value) => setSelectedTemplate(value)}
                isClearable={true}
                isLoading={isLoading}
              />
            </Grid>
          )}
          {selectionType === 'database' && (
            <Grid item xs={12}>
              <SelectDropdown
                optionsData={databaseOptions}
                selectedValue={selectedTemplate}
                placeholder="Select Database"
                label="Database"
                id="select-database"
                handleSelect={(value) => setSelectedTemplate(value)}
                isClearable={true}
              />
            </Grid>
          )}
          {selectionType === 'custom' && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
              >
                Upload File
                <input
                  type="file"
                  hidden
                  onChange={(e) => setUploadedFile(e.target.files ? e.target.files[0] : null)}
                />
              </Button>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onHide} variant="outlined" color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Save Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleTemplate;
